/**
 * Sign-in identifiers: a mobile number or an email address, as a person types them.
 *
 * Pure and shared, because the server decides what an identifier is and the two apps have to agree
 * with it before the request goes out (the SMS and WhatsApp choice only shows for a number).
 *
 * The default country is the UAE, because that is where every V1 business is. A number with no country
 * code and a leading 0 (050 123 4567) or none (50 123 4567) is read as +971. Anything that already
 * carries a + or a 00 prefix is taken as international.
 */

const UAE = "971";

export function normalisePhone(raw: string, defaultCountry: string = UAE): string | null {
  let digits = raw.replace(/[^\d+]/g, "");
  if (!digits) return null;

  if (digits.startsWith("00")) digits = `+${digits.slice(2)}`;

  if (digits.startsWith("+")) {
    const rest = digits.slice(1);
    if (rest.length < 7 || rest.length > 15) return null;
    return `+${rest}`;
  }

  // A UAE number typed with its country code but no plus (971501234567).
  if (digits.startsWith(defaultCountry) && digits.length === defaultCountry.length + 9) {
    return `+${digits}`;
  }

  if (digits.startsWith("0")) digits = digits.slice(1);
  if (digits.length !== 9) return null;
  return `+${defaultCountry}${digits}`;
}

/** `+971 5• ••• 4421`, the spec's masked form for lists. */
export function maskPhone(e164: string): string {
  const match = /^\+(\d{3})(\d)(\d+)(\d{4})$/.exec(e164);
  if (!match) return e164.replace(/\d(?=\d{4})/g, "•");
  const [, country, first, middle, last] = match;
  return `+${country} ${first}• ${"•".repeat(Math.max(0, middle!.length - 1))} ${last}`.replace(/\s+/g, " ");
}

/**
 * Country codes the format below knows: the Gulf and the countries most families here come from.
 * A number from any other country is shown as it is stored (`+...` with no spaces), never split at
 * a wrong place.
 */
const KNOWN_COUNTRY_CODES = [
  "1", "7", "20", "27", "30", "31", "32", "33", "34", "39", "41", "44", "49", "61", "62", "63", "64",
  "65", "66", "81", "82", "86", "90", "91", "92", "93", "94", "98", "212", "213", "216", "218",
  "249", "254", "880", "961", "962", "963", "964", "965", "966", "967", "968", "970", "972",
  "973", "974", "977",
];

/**
 * Country calling codes are prefix free (ITU-T E.164): `1` and `7` are the only codes of one digit, the
 * two-digit codes are listed below, and every other code is three digits that start with one of the
 * three-digit zones. So the code of any well formed number is read without a full country table.
 */
const ONE_DIGIT_CODES = ["1", "7"];
const TWO_DIGIT_CODES = [
  "20", "27", "30", "31", "32", "33", "34", "36", "39", "40", "41", "43", "44", "45", "46", "47", "48",
  "49", "51", "52", "53", "54", "55", "56", "57", "58", "60", "61", "62", "63", "64", "65", "66", "81",
  "82", "84", "86", "90", "91", "92", "93", "94", "95", "98",
];
const THREE_DIGIT_ZONES = [
  "21", "22", "23", "24", "25", "26", "29", "35", "37", "38", "42", "50", "59", "67", "68", "69", "80",
  "85", "87", "88", "96", "97", "99",
];

/** The country calling code of a number, `+971`, for counts by country. A malformed number is `other`. */
export function countryCode(e164: string): string {
  if (!/^\+\d{8,15}$/.test(e164)) return "other";
  const digits = e164.slice(1);
  if (ONE_DIGIT_CODES.includes(digits.slice(0, 1))) return `+${digits.slice(0, 1)}`;
  if (TWO_DIGIT_CODES.includes(digits.slice(0, 2))) return `+${digits.slice(0, 2)}`;
  if (THREE_DIGIT_ZONES.includes(digits.slice(0, 2))) return `+${digits.slice(0, 3)}`;
  return "other";
}

/**
 * A full number for people to read: `+971 50 112 4421` for a UAE mobile, `+971 4 333 4444` for a
 * UAE landline, the country code and groups of three digits (the last group three or four) for a
 * known country. For the owner's own lists, where the owner has the number already (the WhatsApp
 * sync tool shows it in full).
 */
export function formatPhone(e164: string): string {
  const uaeMobile = /^\+971(5\d)(\d{3})(\d{4})$/.exec(e164);
  if (uaeMobile) return `+971 ${uaeMobile[1]} ${uaeMobile[2]} ${uaeMobile[3]}`;
  const uaeLandline = /^\+971([2-9])(\d{3})(\d{4})$/.exec(e164);
  if (uaeLandline) return `+971 ${uaeLandline[1]} ${uaeLandline[2]} ${uaeLandline[3]}`;
  if (!/^\+\d{8,15}$/.test(e164)) return e164;
  const digits = e164.slice(1);
  const country = [3, 2, 1].map((length) => digits.slice(0, length)).find((code) => KNOWN_COUNTRY_CODES.includes(code));
  if (!country) return e164;
  const rest = digits.slice(country.length);
  const groups: string[] = [];
  let index = 0;
  while (rest.length - index > 4) {
    groups.push(rest.slice(index, index + 3));
    index += 3;
  }
  groups.push(rest.slice(index));
  return `+${country} ${groups.join(" ")}`;
}

/** `a•••@example.com`, for a notice that goes to a different channel than the address itself. */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf("@");
  if (at < 1) return email;
  return `${email[0]}•••${email.slice(at)}`;
}

/**
 * Lowercase and trimmed, or null when it is not an address. Deliberately loose: one `@`, something
 * before it, a dot after it, no spaces. The code we send is the real check.
 */
export function normaliseEmail(raw: string): string | null {
  const value = raw.trim().toLowerCase();
  if (value.length > 254) return null;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return null;
  return value;
}

export type IdentifierKind = "phone" | "email";

export type ClassifiedIdentifier = { kind: IdentifierKind; value: string };

/** What the person is typing, before it is complete. An `@` means email; anything else is a number. */
export function identifierKindOf(raw: string): IdentifierKind {
  return raw.includes("@") ? "email" : "phone";
}

/**
 * The server's rule: an `@` makes it an email, anything else goes through the phone normaliser.
 * Null when it is neither.
 */
export function classifyIdentifier(raw: string): ClassifiedIdentifier | null {
  if (identifierKindOf(raw) === "email") {
    const email = normaliseEmail(raw);
    return email ? { kind: "email", value: email } : null;
  }
  const phone = normalisePhone(raw);
  return phone ? { kind: "phone", value: phone } : null;
}

/** What a list shows in place of a phone number the person has not given. */
export const NO_MOBILE_NUMBER = "No mobile number";

/** A masked number for a list, or the words that say there is none. */
export function maskPhoneOrNone(phoneE164: string | null): string {
  return phoneE164 ? maskPhone(phoneE164) : NO_MOBILE_NUMBER;
}

/** A name for a person with no name yet: the masked number, else the address, else "Member". */
export function contactLabel(person: { phoneE164: string | null; email?: string | null }): string {
  if (person.phoneE164) return maskPhone(person.phoneE164);
  if (person.email) return person.email;
  return "Member";
}

/** The masked form a notice shows. */
export function maskIdentifier(identifier: ClassifiedIdentifier): string {
  return identifier.kind === "phone" ? maskPhone(identifier.value) : maskEmail(identifier.value);
}

/** Sign-in codes are six digits on every channel. */
export const SIGN_IN_CODE_LENGTH = 6;

/**
 * The code in whatever the person pasted or the keyboard filled in: "123456", "123 456",
 * "123-456", or a whole message such as "Your Teamna verification code is: 123456." A lone run of
 * exactly six digits wins; otherwise the digits in order, cut to six, so typing one digit at a time
 * still builds the code.
 */
export function codeFromText(text: string): string {
  // No lookbehind: iOS Safari before 16.4 cannot parse it, and one bad regex breaks the bundle.
  const lone = text.match(/(?:^|\D)(\d{6})(?!\d)/);
  if (lone?.[1]) return lone[1];
  const grouped = text.match(/(?:^|\D)(\d{3})[\s-](\d{3})(?!\d)/);
  if (grouped?.[1] && grouped[2]) return grouped[1] + grouped[2];
  return text.replace(/\D/g, "").slice(0, SIGN_IN_CODE_LENGTH);
}

/**
 * The reserved domain of demo accounts spawned from Control Center. Nobody receives mail there, and
 * the API never sends to it: a demo account signs in with the fixed code Control Center shows. A
 * sign-in on this domain never creates an account, so only Control Center can make one.
 */
export const DEMO_EMAIL_DOMAIN = "demo.teamna.app";

/** True for an address on the demo domain. Expects the normalised (lowercase, trimmed) form. */
export function isDemoEmail(email: string | null | undefined): boolean {
  return typeof email === "string" && email.endsWith(`@${DEMO_EMAIL_DOMAIN}`);
}
